import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'
import { Path, useFormContext } from 'react-hook-form'

import { RECEIPT_INFORMATION_FIELDS } from '@/features/update-receipt-information'

import { TReceiptInformationForm } from '../model/form'

type ReceiptInformationValidationContextState = {
  shouldValidateReceiptForm: () => boolean
  handleReceiptFormOpen: () => void
  receiptTrigger: () => Promise<boolean>
}

export const ReceiptInformationValidationContext =
  createContext<ReceiptInformationValidationContextState | null>(null)

type Props = {
  children: ReactNode
}

export const ReceiptInformationValidationProvider = ({ children }: Props) => {
  const isReceiptFormOpen = useRef(false)

  const { clearErrors, trigger } = useFormContext<TReceiptInformationForm>()

  const shouldValidateReceiptForm = useCallback(() => {
    return isReceiptFormOpen.current
  }, [])

  const handleReceiptFormOpen = useCallback(() => {
    isReceiptFormOpen.current = true
  }, [])

  const receiptTrigger = useCallback(async () => {
    const fields = RECEIPT_INFORMATION_FIELDS as Path<TReceiptInformationForm>[]
    clearErrors(fields)

    const result = await trigger(fields, {
      shouldFocus: true,
    })

    return result
  }, [clearErrors, trigger])

  const value: ReceiptInformationValidationContextState = useMemo(
    () => ({
      shouldValidateReceiptForm,
      handleReceiptFormOpen,
      receiptTrigger,
    }),
    [shouldValidateReceiptForm, handleReceiptFormOpen, receiptTrigger],
  )

  return (
    <ReceiptInformationValidationContext.Provider value={value}>
      {children}
    </ReceiptInformationValidationContext.Provider>
  )
}

export const useReceiptInformationValidation = () => {
  const context = useContext(ReceiptInformationValidationContext)

  if (!context) {
    throw new Error(
      'useReceiptInformationValidation must be used within a ReceiptInformationValidationProvider',
    )
  }

  return context
}
